import { Edit, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { DataListCard, type DataListColumn, type DataListAction } from '@/components/common/DataListCard';
import type { UserDto } from '../types';

interface UserListProps {
  users: UserDto[];
  onEdit: (user: UserDto) => void;
  onDeactivate: (user: UserDto) => void;
  isLoading?: boolean;
  canEdit?: boolean;
}

export function UserList({
  users,
  onEdit,
  onDeactivate,
  isLoading = false,
  canEdit = false,
}: UserListProps) {
  const columns: DataListColumn<UserDto>[] = [
    {
      key: 'name',
      label: 'Name',
      render: (user) => user.fullName,
    },
    {
      key: 'username',
      label: 'Username',
      render: (user) => user.username,
    },
    {
      key: 'email',
      label: 'Email',
      render: (user) => user.email || '-',
    },
    {
      key: 'role',
      label: 'Role',
      render: (user) => (
        <Badge variant="outline">{user.roleName}</Badge>
      ),
    },
    {
      key: 'commission',
      label: 'Commission %',
      render: (user) => `${user.commissionRate}%`,
    },
    {
      key: 'status',
      label: 'Status',
      render: (user) => (
        <Badge variant={user.isActive ? 'default' : 'secondary'}>
          {user.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
  ];

  const actions: DataListAction<UserDto>[] = [
    {
      label: '',
      icon: <Edit className="h-4 w-4" />,
      onClick: onEdit,
      title: 'Edit user',
      canShow: () => canEdit,
    },
    {
      label: '',
      icon: <Trash2 className="h-4 w-4 text-red-500" />,
      onClick: onDeactivate,
      canShow: (user) => canEdit && user.isActive,
      title: 'Deactivate user',
    },
  ];

  return (
    <DataListCard
      items={users}
      columns={columns}
      actions={actions}
      keyExtractor={(user) => user.id}
      isLoading={isLoading}
      loadingMessage="Loading users..."
      emptyMessage="No users found."
    />
  );
}
